import { Controller } from "@hotwired/stimulus"
import { calculateSimulatedRS } from "../services/relative_strength"

// Landing-page hero animation: an uptrending "price line" over a gradient
// sky, with a spring-physics wave driving the nodes and a live simulated RS
// pill tracking the line. Drawn with the raw Canvas 2D API — this used to be
// Paper.js, but everything here is primitives (polyline, polygon, triangle,
// rounded pill, text), so the 332KB paper bundle bought nothing.
export default class extends Controller {
  static targets = ["canvas"]

  connect() {
    // Skip the per-frame canvas animation for reduced-motion users and phone
    // viewports (battery/CPU); the body's CSS gradient fallback stands in
    // (see pages/_landing.scss).
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.innerWidth < 768) {
      return
    }

    const canvas = this.canvasTarget
    canvas.width = this.element.offsetWidth || window.innerWidth
    canvas.height = window.innerHeight

    this.startAnimation(canvas)
  }

  disconnect() {
    if (this.frameId) {
      cancelAnimationFrame(this.frameId)
      this.frameId = null
    }
  }

  startAnimation(canvas) {
    const ctx = canvas.getContext("2d")
    const width = canvas.width
    const height = canvas.height

    // Colors
    const lightBlue = "#5B9BD5"
    const lighterBlue = "#8FC1E8"
    const navyBlue = "#1F3864"
    const lineColor = "#000000"

    // Gradient for the sky area above the line
    const skyGradient = ctx.createLinearGradient(width / 2, 0, width / 2, height * 0.6)
    skyGradient.addColorStop(0, lighterBlue)
    skyGradient.addColorStop(1, lightBlue)

    // One node per RS price sample: the simulated RS uses 5 prices (t0..t4
    // across 4 lookback periods), so the line has exactly 5 nodes.
    // Each node owns a vertical band around a rising baseline; bands barely
    // overlap between neighbours, so the line always reads as an uptrend no
    // matter where each node sits within its band.
    const nodeConfigs = [
      { baseline: 0.78, halfBand: 0.10, speed: 0.85 },
      { baseline: 0.61, halfBand: 0.12, speed: 1.15 },
      { baseline: 0.51, halfBand: 0.13, speed: 0.95 },
      { baseline: 0.37, halfBand: 0.12, speed: 1.25 },
      { baseline: 0.26, halfBand: 0.08, speed: 0.75 }
    ].map((cfg, i, arr) => ({
      x: (i / (arr.length - 1)) * width,
      baselineY: cfg.baseline * height,
      minY: (cfg.baseline - cfg.halfBand) * height,
      maxY: (cfg.baseline + cfg.halfBand) * height,
      // Amplitude fills the band exactly — the wave target never saturates
      // against a clamp, so no node parks at the top of the screen
      amplitude: cfg.halfBand * height,
      speed: cfg.speed,
      phase: i * 1.7 + Math.random() * 0.8
    }))

    // Node Y positions, starting on each baseline
    const ys = nodeConfigs.map(node => node.baselineY)

    // Spring physics state for tension-based movement
    const velocities = new Array(nodeConfigs.length).fill(0)

    // Physics constants
    const springForce = 0.03        // Spring force toward wave target
    const damping = 0.95            // Energy loss per frame
    const neighborInfluence = 0.01  // Tension between neighbors
    const maxVelocity = 1.2         // Maximum point velocity (px per 60fps frame)
    const waveSpeed = 0.3           // Wave oscillation speed (radians per second)
    const bounceFactor = 0.5        // Energy retained on band-edge bounce

    // RS indicator state (smoothly eased toward the live simulated value)
    const rsPillWidth = 90
    const rsPillHeight = 40
    let currentRS = 0

    let time = 0
    let lastTimestamp = null

    const stepPhysics = (delta) => {
      time += delta

      // Normalize delta to 60fps for consistent physics
      const deltaScale = delta * 60

      for (let i = 0; i < ys.length; i++) {
        const node = nodeConfigs[i]
        const currentY = ys[i]

        // 1. Target position: baseline + layered sine drift confined to the
        // node's band. Two components with different frequencies keep the
        // motion organic; their weights sum to 1 so the target stays in-band.
        const t = time * waveSpeed * node.speed
        const wave = Math.sin(t + node.phase) * 0.65 +
                     Math.sin(t * 1.7 + node.phase * 2.3) * 0.35
        const targetY = node.baselineY + wave * node.amplitude

        // 2. Spring force toward the wave target
        const displacement = (targetY - currentY) * springForce

        // 3. Light neighbor influence for coherent movement (interior only)
        let neighborForce = 0
        if (i > 0 && i < ys.length - 1) {
          const avgNeighborY = (ys[i - 1] + ys[i + 1]) / 2
          neighborForce = (avgNeighborY - currentY) * neighborInfluence
        }

        // 4. Update velocity (scaled by delta for frame-rate independence)
        velocities[i] += (displacement + neighborForce) * deltaScale

        // 5. Apply damping (adjusted for variable time step)
        velocities[i] *= Math.pow(damping, deltaScale)

        // 6. Clamp velocity
        velocities[i] = Math.max(-maxVelocity, Math.min(maxVelocity, velocities[i]))

        // 7. Update position (velocity already scaled by deltaScale)
        let newY = currentY + velocities[i]

        // 8. Keep the node inside its band with a soft bounce — the bands
        // are what guarantee the uptrend, so they are hard limits
        if (newY < node.minY) {
          newY = node.minY
          velocities[i] = Math.abs(velocities[i]) * bounceFactor
        } else if (newY > node.maxY) {
          newY = node.maxY
          velocities[i] = -Math.abs(velocities[i]) * bounceFactor
        }

        ys[i] = newY
      }
    }

    // Simulated RS from node positions: lower Y = higher on screen = higher
    // price. Strength bounds calibrated (by simulation) to the node bands
    // above so a rising line reads like a leader: median RS ~92, above 90 half
    // the time, occasional dips to the 70s-80s, never below 50.
    const easeRS = () => {
      const prices = ys.map(y => height - y)
      const targetRS = calculateSimulatedRS(prices, { minStrength: 0.85, maxStrength: 1.95 })
      currentRS += (targetRS - currentRS) * 0.08
    }

    const drawBackground = () => {
      ctx.fillStyle = navyBlue
      ctx.fillRect(0, 0, width, height)

      // Sky polygon: screen top edge down to the line
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(width, 0)
      for (let i = ys.length - 1; i >= 0; i--) {
        ctx.lineTo(nodeConfigs[i].x, ys[i])
      }
      ctx.closePath()
      ctx.fillStyle = skyGradient
      ctx.fill()
    }

    const drawLine = () => {
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(nodeConfigs[0].x, ys[0])
      for (let i = 1; i < ys.length; i++) {
        ctx.lineTo(nodeConfigs[i].x, ys[i])
      }
      ctx.strokeStyle = lineColor
      ctx.lineWidth = 20
      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
      ctx.shadowBlur = 40
      ctx.shadowOffsetX = 8
      ctx.shadowOffsetY = 8
      ctx.stroke()
      ctx.restore()
    }

    const drawArrow = () => {
      const last = ys.length - 1
      const dx = nodeConfigs[last].x - nodeConfigs[last - 1].x
      const dy = ys[last] - ys[last - 1]
      const angle = Math.atan2(dy, dx)

      const arrowSize = 40
      const arrowWidth = 25

      // Arrow tip extends beyond the line end
      const tipX = nodeConfigs[last].x + arrowSize * 0.3 * Math.cos(angle)
      const tipY = ys[last] + arrowSize * 0.3 * Math.sin(angle)

      // Base center sits back along the tangent; the two base corners are
      // equidistant along the perpendicular, so the head is symmetrical
      const baseX = tipX - arrowSize * Math.cos(angle)
      const baseY = tipY - arrowSize * Math.sin(angle)
      const perpX = -Math.sin(angle)
      const perpY = Math.cos(angle)

      ctx.beginPath()
      ctx.moveTo(baseX + arrowWidth * perpX, baseY + arrowWidth * perpY)
      ctx.lineTo(tipX, tipY)
      ctx.lineTo(baseX - arrowWidth * perpX, baseY - arrowWidth * perpY)
      ctx.closePath()
      ctx.fillStyle = lineColor
      ctx.strokeStyle = lineColor
      ctx.lineWidth = 12
      ctx.fill()
      ctx.stroke()
    }

    const drawRSPill = () => {
      // Near the arrow, above the line by default but flipped below it when
      // the line end is close to the top of the screen, and always clamped
      // fully on-canvas so it never disappears
      const lastX = nodeConfigs[nodeConfigs.length - 1].x
      const lastY = ys[ys.length - 1]
      const edgePadding = 12
      let pillX = lastX - 70
      let pillY = lastY - 55
      if (pillY < rsPillHeight / 2 + edgePadding) {
        pillY = lastY + 65
      }
      pillX = Math.max(rsPillWidth / 2 + edgePadding, Math.min(width - rsPillWidth / 2 - edgePadding, pillX))
      pillY = Math.max(rsPillHeight / 2 + edgePadding, Math.min(height - rsPillHeight / 2 - edgePadding, pillY))

      // (pillX, pillY) is the pill's center; drawing is from the top-left
      const left = pillX - rsPillWidth / 2
      const top = pillY - rsPillHeight / 2

      ctx.beginPath()
      ctx.roundRect(left, top, rsPillWidth, rsPillHeight, rsPillHeight / 2)
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)"
      ctx.fill()

      ctx.textBaseline = "alphabetic"
      ctx.fillStyle = "#888888"
      ctx.font = "bold 14px system-ui, -apple-system, sans-serif"
      ctx.fillText("RS", left + 12, top + 26)

      if (currentRS >= 90) {
        ctx.fillStyle = "#4ADE80" // Green for elite
      } else if (currentRS >= 80) {
        ctx.fillStyle = "#FBBF24" // Yellow for strong
      } else {
        ctx.fillStyle = "#F87171" // Red for everything below
      }
      ctx.font = "bold 18px system-ui, -apple-system, sans-serif"
      ctx.fillText(String(Math.round(currentRS)), left + 38, top + 27)
    }

    const frame = (timestamp) => {
      // Delta in seconds, capped so a background-tab suspension (rAF stops
      // entirely in hidden tabs) doesn't land as one giant physics step
      const delta = lastTimestamp === null ? 1 / 60 : Math.min((timestamp - lastTimestamp) / 1000, 0.1)
      lastTimestamp = timestamp

      stepPhysics(delta)
      easeRS()

      drawBackground()
      drawLine()
      drawArrow()
      drawRSPill()

      this.frameId = requestAnimationFrame(frame)
    }

    this.frameId = requestAnimationFrame(frame)
  }
}
